import FileUploadIcon from "@mui/icons-material/FileUpload";
import LoadingButton from "@mui/lab/LoadingButton";
import { useSnackbar } from "notistack";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import useFileUpload from "../../../hooks/useFileUpload";
import {
  AssociateValidationErrors,
  useImportAssociatesMutation,
} from "../../../redux/api/AssociatesApi";
import ImportErrorDialog from "../../modal/ImportErrorDialog";
import * as React from "react";
import { Tooltip } from "@mui/material";
import { TooltipTable } from "../../../common/styles";
import { useSelectedOrganization } from "../../../hooks/useSelectedOrganization";

const columnNames = ["name", "emailAddress"] as const;
type ColumnName = typeof columnNames[number];
const columns: Record<ColumnName, { translation: string; required: boolean }> =
  {
    name: {
      translation: "associate.name",
      required: true,
    },
    emailAddress: {
      translation: "associate.email",
      required: true,
    },
  };

const UploadButton = () => {
  const { t } = useTranslation();
  const [importAssociates, { isLoading }] = useImportAssociatesMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { enqueueSnackbar } = useSnackbar();
  const organization = useSelectedOrganization();

  const [modalOpen, setModalOpen] = React.useState(false);
  const [validationErrors, setValidationErrors] =
    React.useState<AssociateValidationErrors>({});
  const handleModalClose = () => {
    setModalOpen(false);
  };

  const title = t("upload.tooltip");

  const tooltip = t("associates.upload.tooltip");

  const onFileSelected = async (files: File[] | FileList) => {
    try {
      const associates = await importAssociates({
        file: files[0],
        organizationId: organization.publicId,
      }).unwrap();
      enqueueSnackbar(
        t("upload.successful", {
          count: associates.length,
        }),
        {
          variant: "success",
        }
      );
    } catch (err: any) {
      let message = "upload.failed";
      if (err.data?.message) {
        if (err.data?.validationErrors) {
          setValidationErrors(err.data?.validationErrors);
          setModalOpen(true);
        }
        message = err.data?.message;
      }
      enqueueSnackbar(t(message), {
        variant: "error",
        key: err.status,
        preventDuplicate: true,
      });
    }
  };

  const { openDialog } = useFileUpload({
    inputRef: fileInputRef,
    onFileSelected,
  });

  return (
    <div id="upload-associates-button">
      <Tooltip
        title={
          <>
            {tooltip}
            <TooltipTable>
              <tbody>
                <tr>
                  {columnNames.map((name) => (
                    <th key={name}>
                      {name}
                      {columns[name].required ? "*" : ""}
                    </th>
                  ))}
                </tr>
                <tr>
                  {columnNames.map((name) => (
                    <td key={name}>{t(columns[name].translation)}</td>
                  ))}
                </tr>
              </tbody>
            </TooltipTable>
          </>
        }
        placement="top"
      >
        <LoadingButton
          variant="outlined"
          size="medium"
          onClick={openDialog}
          startIcon={<FileUploadIcon />}
          loading={isLoading}
          loadingPosition="start"
          color="secondary"
        >
          {title}
        </LoadingButton>
      </Tooltip>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.ldif"
        multiple={false}
        hidden
      />
      <ImportErrorDialog
        onClose={handleModalClose}
        open={modalOpen}
        entries={validationErrors}
      />
    </div>
  );
};

export default UploadButton;
